import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "../../../ui-components/table/table";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import LabeledTextInput from "../../../ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import { LabelModes } from "../../../ui-components/molecules/labeled-inputs/labeledInput";
import UsersState from "../../../slices/customers/users/TeamUsersState";
import {
  getUsers,
  resetErrors,
  usersSelector
} from "../../../slices/customers/users/teamUsersSlice";
import UserDetails from "../../company/user-details/userDetails";
import InviteUser from "../../invite-user/inviteUser";
import UserProfile from "../../../app/data/user/UserProfile";
import { userStatuses } from "../../company/constants";
import { customerRoles } from "../../../app/data/common/customerRoles";
import "./companySettingsUsers.scss";

const columnHelper = createColumnHelper<any>();

const CompanySettingsUsers: React.FC<{}> = () => {
  const [userId, setUserId] = useState<string | undefined>();
  const [showInviteUser, setShowInviteUser] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>("");
  const dispatch = useDispatch();
  const usersState: UsersState = useSelector(usersSelector);

  const columns = [
    columnHelper.accessor("dateCreated", {
      id: "dateCreated",
      header: "Sign-up date",
      cell: (cellProps) => (
        <>
          {cellProps.getValue().toUiDateFormat()}
        </>
      ),
      size: 120,
    }),
    columnHelper.accessor("name", {
      id: "name",
      header: "Name",
      size: 200,
    }),
    columnHelper.accessor("title", {
      id: "title",
      header: "Title",
      size: 140,
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: "Email",
      size: 200
    }),
    columnHelper.accessor("phoneNumber", {
      id: "phoneNumber",
      header: "Phone",
      size: 200
    }),
    columnHelper.accessor("role", {
      id: "role",
      header: "Role",
      size: 120,
      cell: (cellProps) => (
        <>
          {customerRoles.find(role => role.value === cellProps.getValue())?.label}
        </>
      )
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => (
        <div style={{ textAlign: "center" }}>
          Status
        </div>
      ),
      cell: (cellProps) => (
        <div
          className="xgs-company__users__details__status__circle"
          style={{
            backgroundColor: userStatuses.find(status => status.value === cellProps.getValue())?.color,
            marginLeft: "auto",
            marginRight: "auto"
          }}
          title={cellProps.getValue().toLowerCase()}>
        </div>
      ),
      size: 70,
    })
  ];

  const onListInfiniteScroll = () => {
    dispatch(getUsers("current", false, undefined, usersState.usersRequest?.lastIds));
  };

  const onUserClick = (row: UserProfile) => {
    setUserId(row.id)
  };

  const onSearchStart = () => {
    dispatch(getUsers("current", false, searchString));
    setUserId(undefined);
  };

  const onSearchClear = () => {
    setSearchString("");
    dispatch(getUsers("current", false, undefined));
    setUserId(undefined);
  };

  useEffect(() => {
    dispatch(resetErrors());
    dispatch(getUsers("current", false, undefined));

    return () => {
      dispatch(resetErrors());
    }
  }, [dispatch]);

  return (
    <div className="xgs-company__users">
      <div className="xgs-list__controls xgs-company__users__controls">
        <div className="xgs-list__controls__search">
          <LabeledTextInput
            labelMode={LabelModes.column}
            className="xgs-list__controls__search__input"
            value={searchString}
            onChange={(e) => setSearchString(e.currentTarget.value)}
            onKeyDown={(e) => (e.key === "Enter") && !(usersState.fetch_was_started && usersState.requestCreator === "GET_USERS") && onSearchStart()}
            type="text"
            placeholder="First, last name, email"
          />
          <Button
            className="xgs-list__controls__button"
            theme={ButtonThemes.blue}
            disabled={usersState.fetch_was_started && usersState.requestCreator === "GET_USERS"}
            onClick={onSearchStart}>
            Search
          </Button>
          <Button
            className="xgs-list__controls__button"
            theme={ButtonThemes.gray}
            onClick={onSearchClear}>
            Clear
          </Button>
        </div>
        <Button
          className="xgs-company__users__invite-button"
          theme={ButtonThemes.blue}
          onClick={() => { setShowInviteUser(true); }}>
          Invite new user
        </Button>
        <InviteUser
          show={showInviteUser}
          onInviteSent={() => {
            dispatch(getUsers("current", false, undefined));
          }}
          onClose={() => {
            setShowInviteUser(false);
          }}
        />
      </div>
      {usersState.fetch_was_failed && usersState.requestCreator === "GET_USERS" && (
        <XGSErrorMessage>{usersState.fetch_fail_reason}</XGSErrorMessage>
      )}
      {!(usersState.fetch_was_failed && usersState.requestCreator === "GET_USERS") && (
        <Table
          isLoading={usersState.fetch_was_started && usersState.requestCreator === "GET_USERS"}
          columns={columns}
          data={usersState.users}
          onRowClicked={onUserClick}
          cursorPointer={true}
          rowHeight={66}
          minTableHeight={420}
          noResultsText="There are no users"
          infiniteScroll={true}
          infiniteScrollLoading={usersState.fetchPortion_was_started}
          infiniteScrollHasNext={!usersState.usersFetchedAll}
          onInfiniteScroll={onListInfiniteScroll}
        />
      )}
      <UserDetails
        id={userId}
        show={!!userId}
        onClose={() => {
          setUserId(undefined);
        }}
        onDelete={() => {
          dispatch(getUsers("current", false, undefined));
        }}
        customer={true}
      />
    </div>
  );
};

export default CompanySettingsUsers;
